import { CONTENTFUL_COMPONENTS } from "@contentful/experiences-sdk-react";
import {
  SectionWithBackground,
  sectionWithBackgroundDefinition,
  Button,
  baseButtonDefinition,
  InvocaButton,
  invocaButtonDefinition,
  CrestaChatEmbed,
  crestaChatEmbedDefinition,
  Badge,
  badgeDefinition,
  Heading,
  headingDefinition,
  BodyText,
  bodyTextDefinition,
  LazyGoogleMaps,
  googleMapsDefinition,
  LazyGoogleMapsOnly,
  googleMapsOnlyDefinition,
  SmartMedia,
  smartMediaDefinition,
  HalfScreen,
  HalfScreenDefinition,
  PDP,
  PDPDefinition,
  Carousel,
  CarouselDefinition,
  CarouselSlide,
  CarouselSlideDefinition,
  RatingStars,
  ratingStarsDefinition,
  BrandfolderIcon,
  BrandfolderIconDefinition,
  MuiIcon,
  MuiIconDefinition,
  BrandfolderImage,
  BrandfolderImageDefinition,
  FaqAccordion,
  faqAccordionDefinition,
  Quiz,
  quizDefinition,
  QuizButton,
  quizButtonDefinition,
  Ticker,
  tickerDefinition,
  LinkWrapper,
  baseLinkWrapperDefinition,
  LazyLocationSearch,
  locationSearchDefinition,
  PopLazyLocationSearch,
  PopLocationSearchDefinition,
  LongForm,
  longFormDefinition,
  LongFormItem,
  longFormItemDefinition,
  SegmentsContainer,
  segmentsContainerDefinition,
  SegmentItem,
  segmentItemDefinition,
  List,
  listDefinition,
  PlayButton,
  playButtonDefinition,
  VideoBackground,
  videoBackgroundDefinition,
  ImageBackground,
  imageBackgroundDefinition,
  MediaGallery,
  mediaGalleryDefinition,
  ExperimentContainer,
  experimentContainerDefinition,
  ExperimentVariationItem,
  experimentVariationItemDefinition,
  AppointmentCard,
  appointmentCardDefinition,
  ReviewsSummary,
  reviewsSummaryDefinition,
  ReviewCard,
  reviewCardDefinition,
  ReviewsCarousel,
  reviewsCarouselDefinition,
  ProviderCard,
  providerCardDefinition,
  SeeAllReviews,
  seeAllReviewsDefinition,
  IFrame,
  iFrameDefinition,
  FacilityDataList,
  facilityDataListDefinition,
  ShowBasedOnData,
  showBasedOnDataDefinition,
  ReusableContent,
  reusableContentDefinition,
  Modal,
  modalDefinition,
  StickyNav,
  stickyNavDefinition,
  StickyNavItem,
  stickyNavItemDefinition,
  ScrollToTop,
  scrollToTopDefinition,
  schedulingWidgetPresetRegistrations,
  threeStepSchedulerPresetRegistrations,
  SmileHeader,
  smileHeaderDefinition,
  SmileFooter,
  smileFooterAtomDefinition,
  SmileButton,
  smileButtonDefinition,
  SmileLink,
  smileLinkDefinition,
  SmileOfficeCard,
  smileOfficeCardDefinition,
} from "@/app/components";

const OPTIONS = {
  withEditorMode: {
    options: {
      enableEditorProperties: {
        isEditorMode: true,
      },
    },
  },
  withoutWrapper: {
    options: {
      wrapComponent: false,
    },
  },
  withoutWrapperAndEditorMode: {
    options: {
      wrapComponent: false,
      enableEditorProperties: {
        isEditorMode: true,
      },
    },
  },
};

/**
 * The single source of truth for every custom component registered in Studio.
 *
 * Consumed by:
 *   - `studio-config.ts` — passed to `defineComponents` to register them.
 *   - `app/api/studio-registry/route.ts` — exposed as a JSON manifest so the
 *     Studio Audit app can discover registered components without hand-copying.
 *
 * Add new components here (with their `definition`) and both stay in sync.
 */
export const STUDIO_COMPONENTS = [
  {
    component: ImageBackground,
    definition: imageBackgroundDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: VideoBackground,
    definition: videoBackgroundDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    ...OPTIONS.withoutWrapper,
    component: SectionWithBackground,
    definition: sectionWithBackgroundDefinition,
  },
  {
    component: Heading,
    definition: headingDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    ...OPTIONS.withoutWrapperAndEditorMode,
    component: BodyText,
    definition: bodyTextDefinition,
  },
  {
    ...OPTIONS.withoutWrapperAndEditorMode,
    component: Button,
    definition: baseButtonDefinition,
  },
  {
    ...OPTIONS.withoutWrapperAndEditorMode,
    component: InvocaButton,
    definition: invocaButtonDefinition,
  },
  {
    ...OPTIONS.withoutWrapperAndEditorMode,
    component: CrestaChatEmbed,
    definition: crestaChatEmbedDefinition,
  },
  {
    component: BrandfolderImage,
    definition: BrandfolderImageDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: Badge,
    definition: badgeDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    ...OPTIONS.withoutWrapperAndEditorMode,
    component: LinkWrapper,
    definition: baseLinkWrapperDefinition,
  },
  {
    component: LazyGoogleMaps,
    definition: googleMapsDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: LazyGoogleMapsOnly,
    definition: googleMapsOnlyDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: SmartMedia,
    definition: smartMediaDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: HalfScreen,
    definition: HalfScreenDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: PDP,
    definition: PDPDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: Carousel,
    definition: CarouselDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: CarouselSlide,
    definition: CarouselSlideDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: FaqAccordion,
    definition: faqAccordionDefinition,
  },
  {
    component: RatingStars,
    definition: ratingStarsDefinition,
  },
  {
    component: BrandfolderIcon,
    definition: BrandfolderIconDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: MuiIcon,
    definition: MuiIconDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: Quiz,
    definition: quizDefinition,
  },
  {
    component: QuizButton,
    definition: quizButtonDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: Ticker,
    definition: tickerDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: LazyLocationSearch,
    definition: locationSearchDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: PopLazyLocationSearch,
    definition: PopLocationSearchDefinition,
    ...OPTIONS.withoutWrapper,
  },
  {
    component: LongForm,
    definition: longFormDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: LongFormItem,
    definition: longFormItemDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SegmentsContainer,
    definition: segmentsContainerDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SegmentItem,
    definition: segmentItemDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: List,
    definition: listDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: PlayButton,
    definition: playButtonDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: MediaGallery,
    definition: mediaGalleryDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ExperimentContainer,
    definition: experimentContainerDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: ExperimentVariationItem,
    definition: experimentVariationItemDefinition,
    ...OPTIONS.withEditorMode,
  },
  {
    component: AppointmentCard,
    definition: appointmentCardDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ReviewsSummary,
    definition: reviewsSummaryDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ReviewCard,
    definition: reviewCardDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ReviewsCarousel,
    definition: reviewsCarouselDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ProviderCard,
    definition: providerCardDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SeeAllReviews,
    definition: seeAllReviewsDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: IFrame,
    definition: iFrameDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: FacilityDataList,
    definition: facilityDataListDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ShowBasedOnData,
    definition: showBasedOnDataDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ReusableContent,
    definition: reusableContentDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: Modal,
    definition: modalDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: StickyNav,
    definition: stickyNavDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: StickyNavItem,
    definition: stickyNavItemDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: ScrollToTop,
    definition: scrollToTopDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  // The base Scheduling Widget is intentionally NOT registered: its flow
  // settings (visit reason, questionnaire class, patient type) must not be
  // hand-edited by content authors. Authors get the locked per-appointment
  // presets below instead.
  ...schedulingWidgetPresetRegistrations.map((registration) => ({
    ...registration,
    ...OPTIONS.withoutWrapperAndEditorMode,
  })),
  // Clare Simizon's 3-step accordion scheduler, as locked per-service presets
  // (Dentures / Emergency / Implants / Checkup / Clear Aligners). Same doctrine
  // as the widget presets above: the service identity and booking flow are
  // pinned in the preset and never author-editable; only the facility code is
  // a variable. No dispatcher preset.
  ...threeStepSchedulerPresetRegistrations.map((registration) => ({
    ...registration,
    ...OPTIONS.withoutWrapperAndEditorMode,
  })),
  {
    component: SmileHeader,
    definition: smileHeaderDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SmileOfficeCard,
    definition: smileOfficeCardDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SmileFooter,
    definition: smileFooterAtomDefinition,
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SmileButton,
    definition: smileButtonDefinition,
    // Editor properties so the SDK injects isEditorMode: the atom keeps its
    // click analytics and Invoca swap inert on the Studio canvas.
    ...OPTIONS.withoutWrapperAndEditorMode,
  },
  {
    component: SmileLink,
    definition: smileLinkDefinition,
    ...OPTIONS.withoutWrapper,
  },
];

/** Built-in Contentful Studio components enabled for this brand. */
export const STUDIO_BUILT_IN_COMPONENT_IDS = [
  CONTENTFUL_COMPONENTS.columns.id,
  CONTENTFUL_COMPONENTS.divider.id,
];
